/**
 * useWorkflowStatuses Hook
 * Manages workflow status data fetching with search and pagination
 */

import { useState, useCallback, useEffect } from 'react';
import { workflowService, WorkflowStatusDashboard } from '@/services/features/WorkflowService';
import { useAuth } from '@/contexts/AuthContext';

export interface UseWorkflowStatusesResult {
  workflowStatuses: WorkflowStatusDashboard[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  entriesPerPage: number;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  refetch: () => void;
}

export const useWorkflowStatuses = (): UseWorkflowStatusesResult => {
  const { hasuraToken } = useAuth();
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatusDashboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState<{
    searchValue: string;
  } | null>(null);

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const fetchWorkflowStatuses = useCallback(async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * entriesPerPage;
      
      let result;
      if (activeSearch && activeSearch.searchValue.trim()) {
        result = await workflowService.searchWorkflowStatuses(
          activeSearch.searchValue,
          offset,
          entriesPerPage,
          hasuraToken
        );
      } else {
        result = await workflowService.getWorkflowStatuses(
          offset,
          entriesPerPage,
          hasuraToken
        );
      }

      setWorkflowStatuses(result.workflowStatuses);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workflow statuses');
      console.error('Error fetching workflow statuses:', err);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, currentPage, entriesPerPage, activeSearch]);

  useEffect(() => {
    fetchWorkflowStatuses();
  }, [fetchWorkflowStatuses]);

  const handleSearch = useCallback(() => {
    setActiveSearch({
      searchValue
    });
    setCurrentPage(1);
  }, [searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch(null);
    setCurrentPage(1);
  }, []);

  const handleSetCurrentPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleSetEntriesPerPage = useCallback((entries: number) => {
    setEntriesPerPage(entries);
    setCurrentPage(1);
  }, []);

  return {
    workflowStatuses,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    searchValue,
    setCurrentPage: handleSetCurrentPage,
    setEntriesPerPage: handleSetEntriesPerPage,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch: fetchWorkflowStatuses
  };
};
